import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, DollarSign, Ticket, Eye, TrendingUp, Share2, 
  Download, BarChart3, Users, Calendar, Clock, MapPin,
  Settings, Megaphone, FileText, ExternalLink, Copy, 
  Edit, MoreVertical, ChevronRight, CheckCircle, AlertCircle,
  Plus, Trash2, ToggleLeft, ToggleRight, ShieldCheck
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

// Sidebar navigation items
const sidebarItems = [
  { id: 'overview', label: 'Dashboard', icon: BarChart3 },
  { id: 'page', label: 'Build Page', icon: FileText },
  { id: 'tickets', label: 'Tickets', icon: Ticket },
  { id: 'publish', label: 'Publish', icon: CheckCircle },
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'attendees', label: 'Attendees', icon: Users },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
];

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'indigo' }) => {
  const colorClasses = {
    indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-950 dark:text-green-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-950 dark:text-purple-400',
  };

  return (
    <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colorClasses[color])}>
            <Icon className="w-5 h-5" />
          </div>
          {trend && (
            <Badge variant="secondary" className="bg-green-100 text-green-700 border-0 text-xs">
              <TrendingUp className="w-3 h-3 mr-1" />
              {trend}%
            </Badge>
          )}
        </div>
        <p className="text-2xl font-bold text-foreground">{value}</p>
        <p className="text-sm text-muted-foreground">{title}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
};

const EventDashboard = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { isAuthenticated, isPartner, user, loading: authLoading } = useAuth();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [customUrl, setCustomUrl] = useState('');
  
  // Ticket type management state
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [ticketForm, setTicketForm] = useState({
    name: '',
    description: '',
    price: '',
    quantity: '',
    is_active: true
  });
  const [savingTicket, setSavingTicket] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    
    // Allow both partners and admins to access event dashboard
    if (!isPartner && user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    
    fetchEventData();
  }, [isAuthenticated, isPartner, authLoading, eventId, user]);

  const fetchEventData = async () => {
    try {
      const [eventRes, statsRes, ordersRes] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/partner/events/${eventId}/stats`).catch(() => ({ data: null })),
        api.get(`/partner/events/${eventId}/orders`).catch(() => ({ data: [] }))
      ]);
      
      setEvent(eventRes.data);
      setCustomUrl(eventRes.data?.custom_url || '');
      setStats(statsRes.data || generateStats(eventRes.data));
      setOrders(ordersRes.data || []);
    } catch (error) {
      console.error('Failed to fetch event:', error);
      toast.error('Failed to load event');
      navigate('/partner/events');
    } finally {
      setLoading(false);
    }
  };

  // Generate stats based on event data
  const generateStats = (eventData) => {
    const ticketsSold = eventData?.tickets_sold || 0;
    const price = eventData?.price || 0;
    const capacity = eventData?.capacity || 100;
    const grossSales = ticketsSold * price;
    const platformFee = grossSales * 0.12;
    const netSales = grossSales - platformFee;
    
    return {
      gross_sales: grossSales,
      net_sales: netSales,
      platform_fee: platformFee,
      tickets_sold: ticketsSold,
      capacity: capacity,
      page_views: 0,
      conversion_rate: ticketsSold > 0 ? ((ticketsSold / capacity) * 100).toFixed(1) : 0,
      payout_status: netSales > 0 ? 'available' : 'pending',
      available_payout: netSales,
    };
  };

  const copyEventLink = () => {
    const baseUrl = window.location.origin;
    const eventUrl = event?.custom_url 
      ? `${baseUrl}/e/${event.custom_url}`
      : `${baseUrl}/events/${event?.id}`;
    navigator.clipboard.writeText(eventUrl);
    toast.success('Event link copied!');
  };

  const handlePublishEvent = async () => {
    try {
      await api.put(`/events/${eventId}`, { is_published: true });
      setEvent({ ...event, is_published: true });
      toast.success('Event published successfully!');
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error('Failed to publish event');
    }
  };

  const handleUnpublishEvent = async () => {
    try {
      await api.put(`/events/${eventId}`, { is_published: false });
      setEvent({ ...event, is_published: false });
      toast.success('Event unpublished');
    } catch (error) {
      console.error('Failed to unpublish:', error);
      toast.error('Failed to unpublish event');
    }
  };

  const handleSaveCustomUrl = async (url) => {
    try {
      await api.put(`/events/${eventId}`, { custom_url: url });
      setEvent({ ...event, custom_url: url });
      toast.success('Custom URL saved!');
    } catch (error) {
      console.error('Failed to save URL:', error);
      toast.error('Failed to save custom URL');
    }
  };

  // Ticket Type Management Functions
  const openTicketDialog = (ticket = null) => {
    if (ticket) {
      setEditingTicket(ticket);
      setTicketForm({
        name: ticket.name || '',
        description: ticket.description || '',
        price: ticket.price?.toString() || '',
        quantity: ticket.quantity?.toString() || '',
        is_active: ticket.is_active !== false
      });
    } else {
      setEditingTicket(null);
      setTicketForm({
        name: '',
        description: '',
        price: '',
        quantity: '100',
        is_active: true
      });
    }
    setShowTicketDialog(true);
  };

  const handleSaveTicket = async () => {
    if (!ticketForm.name || !ticketForm.price) {
      toast.error('Please fill in name and price');
      return;
    }

    setSavingTicket(true);
    try {
      const ticketData = {
        name: ticketForm.name,
        description: ticketForm.description || null,
        price: parseFloat(ticketForm.price),
        quantity: parseInt(ticketForm.quantity) || 100,
        is_active: ticketForm.is_active
      };

      if (editingTicket) {
        // Update existing ticket
        await api.put(`/events/${eventId}/ticket-types/${editingTicket.id}`, ticketData);
        toast.success('Ticket type updated');
      } else {
        // Create new ticket
        await api.post(`/events/${eventId}/ticket-types`, ticketData);
        toast.success('Ticket type created');
      }

      // Refresh event data
      const eventRes = await api.get(`/events/${eventId}`);
      setEvent(eventRes.data);
      setShowTicketDialog(false);
    } catch (error) {
      console.error('Failed to save ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to save ticket type');
    } finally {
      setSavingTicket(false);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!confirm('Are you sure you want to delete this ticket type?')) {
      return;
    }

    try {
      await api.delete(`/events/${eventId}/ticket-types/${ticketId}`);
      toast.success('Ticket type deleted');
      
      // Refresh event data
      const eventRes = await api.get(`/events/${eventId}`);
      setEvent(eventRes.data);
    } catch (error) {
      console.error('Failed to delete ticket:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete ticket type');
    }
  };

  const handleToggleTicketActive = async (ticket) => {
    try {
      await api.put(`/events/${eventId}/ticket-types/${ticket.id}`, {
        is_active: !ticket.is_active
      });
      toast.success(`Ticket type ${!ticket.is_active ? 'activated' : 'deactivated'}`);
      
      // Refresh event data
      const eventRes = await api.get(`/events/${eventId}`);
      setEvent(eventRes.data);
    } catch (error) {
      console.error('Failed to toggle ticket:', error);
      toast.error('Failed to update ticket type');
    }
  };

  // Get ticket types from event or create default
  const ticketTypes = event?.ticket_types || [];
  const hasTicketTypes = ticketTypes.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Event Not Found</h2>
          <Button onClick={() => navigate('/partner/events')}>Back to Events</Button>
        </div>
      </div>
    );
  }

  const capacityPercentage = stats ? (stats.tickets_sold / stats.capacity) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex">
      {/* Left Sidebar */}
      <aside className="w-64 bg-white dark:bg-gray-900 border-r flex flex-col fixed h-screen">
        {/* Back Button */}
        <div className="p-4 border-b space-y-2">
          <Button 
            variant="ghost" 
            className="w-full justify-start" 
            onClick={() => navigate('/partner/events')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Events
          </Button>
          {/* Admin Dashboard link for admins */}
          {user?.role === 'admin' && (
            <Button 
              variant="ghost" 
              className="w-full justify-start text-amber-600 hover:text-amber-700 hover:bg-amber-50" 
              onClick={() => navigate('/admin')}
              data-testid="admin-dashboard-link"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              Admin Dashboard
            </Button>
          )}
        </div>

        {/* Event Info */}
        <div className="p-4 border-b">
          <h2 className="font-bold text-foreground line-clamp-2">{event.title}</h2>
          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            {event.date}
          </div>
          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
            <Clock className="w-4 h-4" />
            {event.time}
          </div>
          <Badge 
            className={cn(
              "mt-3",
              event.is_published 
                ? "bg-green-100 text-green-700" 
                : "bg-yellow-100 text-yellow-700"
            )}
          >
            {event.is_published ? 'Published' : 'Draft'}
          </Badge>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'page') {
                    navigate(`/partner/events/${eventId}/builder`);
                  } else {
                    setActiveSection(item.id);
                  }
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-left",
                  isActive 
                    ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400" 
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                )}
                data-testid={`sidebar-${item.id}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm font-medium">{item.label}</span>
                {item.id === 'page' && <ChevronRight className="w-4 h-4 ml-auto" />}
              </button>
            );
          })}
        </nav>

        {/* Quick Actions */}
        <div className="p-4 border-t space-y-2">
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={() => window.open(`/events/${event.id}`, '_blank')}
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview Event
          </Button>
          <Button 
            className="w-full justify-start bg-indigo-600 hover:bg-indigo-700"
            onClick={copyEventLink}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Event
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{event.title}</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-muted-foreground">Event Dashboard</p>
              <Badge className={event.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                {event.is_published ? 'Live' : 'Draft'}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!event.is_published ? (
              <Button 
                onClick={handlePublishEvent}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Publish Event
              </Button>
            ) : (
              <Button variant="outline" onClick={copyEventLink}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            )}
            <Button 
              onClick={() => navigate(`/partner/events/${eventId}/builder`)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Event
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={copyEventLink}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Event Link
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Download className="w-4 h-4 mr-2" />
                  Export Attendees
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate Event
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {event.is_published && (
                  <DropdownMenuItem onClick={handleUnpublishEvent}>
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Unpublish Event
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className="text-red-600">
                  Cancel Event
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Overview Section */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard
                title="Net Sales"
                value={`$${stats?.net_sales?.toFixed(2) || '0.00'}`}
                subtitle="After platform fees"
                icon={DollarSign}
                color="green"
              />
              <MetricCard
                title="Tickets Sold"
                value={stats?.tickets_sold || 0}
                subtitle={`of ${stats?.capacity || 0} available`}
                icon={Ticket}
                color="indigo"
              />
              <MetricCard
                title="Page Views"
                value={stats?.page_views || 0}
                icon={Eye}
                color="blue"
              />
              <MetricCard
                title="Conversion Rate"
                value={`${stats?.conversion_rate || 0}%`}
                icon={TrendingUp}
                color="purple"
              />
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Payout & Capacity */}
              <div className="lg:col-span-2 space-y-6">
                {/* Payout Card */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Payout Summary</CardTitle>
                    <CardDescription>Your earnings from this event</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-muted-foreground">Gross Sales</p>
                        <p className="text-xl font-bold">${stats?.gross_sales?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm text-muted-foreground">Platform Fee (12%)</p>
                        <p className="text-xl font-bold text-red-600">-${stats?.platform_fee?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                        <p className="text-sm text-muted-foreground">Available Payout</p>
                        <p className="text-xl font-bold text-green-600">${stats?.available_payout?.toFixed(2) || '0.00'}</p>
                      </div>
                    </div>
                    {stats?.available_payout > 0 && (
                      <div className="mt-4 flex items-center justify-between p-4 bg-indigo-50 dark:bg-indigo-950 rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium">Funds available for payout</span>
                        </div>
                        <Button size="sm">Request Payout</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Capacity Progress */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Ticket Sales Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold">{stats?.tickets_sold || 0}</span>
                        <span className="text-muted-foreground">of {stats?.capacity || 0} tickets</span>
                      </div>
                      <Progress value={capacityPercentage} className="h-3" />
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{capacityPercentage.toFixed(0)}% sold</span>
                        <span className="text-muted-foreground">{(stats?.capacity - stats?.tickets_sold) || 0} remaining</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Orders */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Recent Orders</CardTitle>
                      <CardDescription>Latest ticket purchases</CardDescription>
                    </div>
                    <Button variant="outline" size="sm">View All</Button>
                  </CardHeader>
                  <CardContent>
                    {orders.length === 0 ? (
                      <div className="text-center py-8">
                        <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">No orders yet</p>
                        <p className="text-sm text-muted-foreground">Orders will appear here as tickets are sold</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Order #</TableHead>
                            <TableHead>Customer</TableHead>
                            <TableHead>Tickets</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {orders.slice(0, 5).map((order) => (
                            <TableRow key={order.id}>
                              <TableCell className="font-mono text-sm">#{order.id.slice(-6)}</TableCell>
                              <TableCell>{order.customer_name}</TableCell>
                              <TableCell>{order.quantity}</TableCell>
                              <TableCell>${order.total?.toFixed(2)}</TableCell>
                              <TableCell className="text-muted-foreground">
                                {format(new Date(order.created_at), 'MMM d, h:mm a')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Quick Actions */}
              <div className="space-y-6">
                {/* Event Link */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Event Link</CardTitle>
                    <CardDescription>Share with your audience</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg mb-3">
                      <p className="text-sm font-mono break-all">
                        {event.custom_url 
                          ? `eventgoers.com/e/${event.custom_url}`
                          : `eventgoers.com/events/${event.id.slice(-8)}`
                        }
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={copyEventLink}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                      <Button variant="outline" className="flex-1">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Sales by Ticket Type */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Sales by Ticket Type</CardTitle>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setActiveSection('tickets')}
                    >
                      <Settings className="w-3 h-3 mr-1" />
                      Manage
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {hasTicketTypes ? (
                        ticketTypes.map((ticket) => (
                          <div 
                            key={ticket.id} 
                            className={cn(
                              "flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors",
                              ticket.is_active 
                                ? "bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700" 
                                : "bg-gray-100 dark:bg-gray-900 opacity-60"
                            )}
                            onClick={() => setActiveSection('tickets')}
                          >
                            <div>
                              <p className={cn("font-medium", !ticket.is_active && "line-through")}>
                                {ticket.name}
                              </p>
                              <p className="text-sm text-muted-foreground">${ticket.price}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{ticket.quantity_sold || 0}</p>
                              <p className="text-sm text-muted-foreground">of {ticket.quantity}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div>
                              <p className="font-medium">General Admission</p>
                              <p className="text-sm text-muted-foreground">${event.price}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold">{stats?.tickets_sold || 0}</p>
                              <p className="text-sm text-muted-foreground">sold</p>
                            </div>
                          </div>
                          {event.gold_price && (
                            <div className="flex items-center justify-between p-3 bg-yellow-50 dark:bg-yellow-950 rounded-lg">
                              <div>
                                <p className="font-medium flex items-center gap-2">
                                  Gold Members
                                  <Badge className="bg-yellow-400 text-yellow-900 text-xs">VIP</Badge>
                                </p>
                                <p className="text-sm text-muted-foreground">${event.gold_price}</p>
                              </div>
                              <div className="text-right">
                                <p className="font-bold">0</p>
                                <p className="text-sm text-muted-foreground">sold</p>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => navigate(`/partner/events/${eventId}/builder`)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Event Page
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Megaphone className="w-4 h-4 mr-2" />
                      Create Marketing Campaign
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Download className="w-4 h-4 mr-2" />
                      Download Reports
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Settings className="w-4 h-4 mr-2" />
                      Event Settings
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}

        {/* Other sections would go here */}
        {activeSection === 'tickets' && (
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Ticket Types</CardTitle>
                  <CardDescription>Manage ticket options for this event</CardDescription>
                </div>
                <Button onClick={() => openTicketDialog()} data-testid="add-ticket-type-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Ticket Type
                </Button>
              </CardHeader>
              <CardContent>
                {hasTicketTypes ? (
                  <div className="space-y-4">
                    {ticketTypes.map((ticket) => (
                      <div 
                        key={ticket.id} 
                        className={cn(
                          "p-4 border rounded-lg",
                          ticket.is_active 
                            ? "bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700" 
                            : "bg-gray-100 dark:bg-gray-950 border-gray-300 dark:border-gray-800 opacity-70"
                        )}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-grow">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className={cn("font-bold text-lg", !ticket.is_active && "line-through")}>
                                {ticket.name}
                              </h4>
                              {!ticket.is_active && (
                                <Badge variant="secondary" className="text-xs">Inactive</Badge>
                              )}
                            </div>
                            {ticket.description && (
                              <p className="text-sm text-muted-foreground mb-3">{ticket.description}</p>
                            )}
                            <div className="flex items-center gap-6 text-sm">
                              <div>
                                <span className="text-muted-foreground">Price:</span>{' '}
                                <span className="font-semibold text-green-600">${ticket.price}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Available:</span>{' '}
                                <span className="font-semibold">{ticket.quantity - (ticket.quantity_sold || 0)}</span>
                                <span className="text-muted-foreground"> of {ticket.quantity}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Sold:</span>{' '}
                                <span className="font-semibold text-indigo-600">{ticket.quantity_sold || 0}</span>
                              </div>
                            </div>
                            <Progress 
                              value={ticket.quantity > 0 ? ((ticket.quantity_sold || 0) / ticket.quantity) * 100 : 0} 
                              className="h-2 mt-3" 
                            />
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleToggleTicketActive(ticket)}
                              title={ticket.is_active ? "Deactivate" : "Activate"}
                            >
                              {ticket.is_active ? (
                                <ToggleRight className="w-5 h-5 text-green-600" />
                              ) : (
                                <ToggleLeft className="w-5 h-5 text-gray-400" />
                              )}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openTicketDialog(ticket)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteTicket(ticket.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              disabled={(ticket.quantity_sold || 0) > 0}
                              title={(ticket.quantity_sold || 0) > 0 ? "Cannot delete - tickets sold" : "Delete"}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                    <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">No Ticket Types Yet</h3>
                    <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                      Create different ticket types like General Admission, VIP, Early Bird, etc. 
                      Each can have its own price and quantity.
                    </p>
                    <Button onClick={() => openTicketDialog()}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Ticket Type
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Default Pricing Info */}
            <Card className="border-0 shadow-sm bg-blue-50 dark:bg-blue-950/20">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100">Default Event Price</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      If no ticket types are configured, the event uses the default price of <strong>${event.price}</strong>.
                      {event.gold_price && (
                        <span> Gold Club members pay <strong>${event.gold_price}</strong>.</span>
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeSection === 'attendees' && (
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Attendees</CardTitle>
                <CardDescription>{stats?.tickets_sold || 0} registered attendees</CardDescription>
              </div>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No attendees yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Ticket Type</TableHead>
                      <TableHead>Purchase Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-medium">{order.customer_name}</TableCell>
                        <TableCell>{order.customer_email}</TableCell>
                        <TableCell>General Admission</TableCell>
                        <TableCell>{format(new Date(order.created_at), 'MMM d, yyyy')}</TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-700">Confirmed</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {activeSection === 'marketing' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Marketing Tools</CardTitle>
              <CardDescription>Promote your event and increase ticket sales</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border cursor-pointer hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <Megaphone className="w-8 h-8 text-indigo-600 mb-3" />
                    <h3 className="font-bold mb-1">AI Marketing Suite</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Generate social posts, emails, and ad copy with AI
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate('/partner/ai-marketing')}
                    >
                      Open AI Suite
                    </Button>
                  </CardContent>
                </Card>
                <Card className="border cursor-pointer hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <Share2 className="w-8 h-8 text-purple-600 mb-3" />
                    <h3 className="font-bold mb-1">Social Sharing</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Share your event across social platforms
                    </p>
                    <Button variant="outline" className="w-full" onClick={copyEventLink}>
                      Get Share Link
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        )}

        {activeSection === 'reports' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Reports & Analytics</CardTitle>
              <CardDescription>Detailed insights about your event performance</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">Detailed reports coming soon</p>
                <Button variant="outline">
                  <Download className="w-4 h-4 mr-2" />
                  Export Basic Report
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {activeSection === 'publish' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Publish Settings</CardTitle>
              <CardDescription>Control when and how your event is visible</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <p className="font-medium">Event Status</p>
                    <p className="text-sm text-muted-foreground">
                      {event.is_published ? 'Your event is live and visible to the public' : 'Your event is in draft mode'}
                    </p>
                  </div>
                  <Badge className={event.is_published ? 'bg-green-500' : 'bg-yellow-500'}>
                    {event.is_published ? 'Published' : 'Draft'}
                  </Badge>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <h3 className="font-medium mb-2">Custom Event URL</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Create a memorable URL for your event
                  </p>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center bg-gray-50 dark:bg-gray-800 rounded-lg px-3">
                      <span className="text-muted-foreground text-sm">eventgoers.com/e/</span>
                      <input 
                        type="text"
                        placeholder="your-event-name"
                        className="flex-1 bg-transparent border-0 outline-none text-sm py-2"
                        value={customUrl}
                        onChange={(e) => setCustomUrl(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                      />
                    </div>
                    <Button onClick={() => handleSaveCustomUrl(customUrl)}>Save URL</Button>
                  </div>
                </div>

                {event.is_published ? (
                  <Button 
                    variant="outline" 
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    onClick={handleUnpublishEvent}
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Unpublish Event
                  </Button>
                ) : (
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={handlePublishEvent}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Publish Event
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Ticket Type Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTicket ? 'Edit Ticket Type' : 'Add Ticket Type'}</DialogTitle>
            <DialogDescription>
              {editingTicket 
                ? 'Update the ticket type details below.' 
                : 'Create a new ticket type for your event.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ticket-name">Ticket Name *</Label>
              <Input
                id="ticket-name"
                placeholder="e.g., General Admission, VIP, Early Bird"
                value={ticketForm.name}
                onChange={(e) => setTicketForm({ ...ticketForm, name: e.target.value })}
                data-testid="ticket-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="ticket-description">Description (Optional)</Label>
              <Textarea
                id="ticket-description"
                placeholder="What's included with this ticket..."
                value={ticketForm.description}
                onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                className="min-h-[80px]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="ticket-price">Price ($) *</Label>
                <Input
                  id="ticket-price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="25.00"
                  value={ticketForm.price}
                  onChange={(e) => setTicketForm({ ...ticketForm, price: e.target.value })}
                  data-testid="ticket-price-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ticket-quantity">Quantity</Label>
                <Input
                  id="ticket-quantity"
                  type="number"
                  min="1"
                  placeholder="100"
                  value={ticketForm.quantity}
                  onChange={(e) => setTicketForm({ ...ticketForm, quantity: e.target.value })}
                  data-testid="ticket-quantity-input"
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium text-sm">Active</p>
                <p className="text-xs text-muted-foreground">Customers can purchase this ticket</p>
              </div>
              <Switch
                checked={ticketForm.is_active}
                onCheckedChange={(checked) => setTicketForm({ ...ticketForm, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowTicketDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTicket}
              disabled={savingTicket || !ticketForm.name || !ticketForm.price}
              data-testid="save-ticket-btn"
            >
              {savingTicket ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                editingTicket ? 'Update Ticket' : 'Create Ticket'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventDashboard;
